import { APIProvider } from "@vis.gl/react-google-maps"
import dynamic from "next/dynamic"
import React, { useCallback, useState, useEffect } from "react"

import { Analytics } from "@bounce/analytics"
import type { Nullable, Coordinates } from "@bounce/util"
import { useIsMobile } from "@bounce/web-components"

import { MapContainer } from "@/components/LuggageStorage/Map/Interactive/MapContainer"
import { MemoizedStoreMarker } from "@/components/LuggageStorage/Map/Interactive/StoreMarker/StoreMarker"
import { config } from "@/config"
import { InteractionEvents } from "@/libs/analytics/events"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"

export type InteractiveMapProps = {
  coordinates: Coordinates
  getStoreUrl: (store: NearbyStorageSpot) => string
  nearestStores: NearbyStorageSpot[]
  initialZoom?: number
  onMapLoaded?: () => void
}

const MOBILE_MAP_LATITUDE_OFFSET = 0.001
const MAX_STORE_MARKER = 50

const InteractiveMapComponent = ({
  coordinates,
  getStoreUrl,
  nearestStores,
  initialZoom,
  onMapLoaded,
}: InteractiveMapProps) => {
  const [selectedStoreId, setSelectedStoreId] = useState<Nullable<string>>(null)
  const [isApiLoaded, setIsApiLoaded] = useState(false)
  const [areTilesLoaded, setAreTilesLoaded] = useState(false)

  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | undefined>(undefined)
  const isMobile = useIsMobile()

  const handleMarkerClick = useCallback(
    (store: NearbyStorageSpot) => {
      setSelectedStoreId(store.id)

      // Calculate offset to provide space for tooltip, more on mobile
      const latOffset = isMobile ? MOBILE_MAP_LATITUDE_OFFSET : 0

      setMapCenter({
        lat: store.coordinates.latitude + latOffset,
        lng: store.coordinates.longitude,
      })

      Analytics.track(InteractionEvents.MapLocationPressed)
    },
    [isMobile],
  )

  useEffect(() => {
    setMapCenter({
      lat: coordinates.latitude,
      lng: coordinates.longitude,
    })
  }, [coordinates])

  useEffect(() => {
    if (isApiLoaded && areTilesLoaded && onMapLoaded) {
      onMapLoaded()
    }
  }, [isApiLoaded, areTilesLoaded, onMapLoaded])

  const onDismiss = useCallback(() => {
    setSelectedStoreId(null)
  }, [])

  const handleApiLoaded = useCallback(() => {
    setIsApiLoaded(true)
  }, [])

  const handleTilesLoaded = useCallback(() => {
    setAreTilesLoaded(true)
  }, [])

  return (
    <div className="h-[260px] md:h-[428px]" data-chromatic="ignore">
      <APIProvider apiKey={config.googleMaps.key} onLoad={handleApiLoaded}>
        <MapContainer
          center={mapCenter}
          defaultZoom={initialZoom}
          initialRegion={{
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
          }}
          onDragStart={() => setMapCenter(undefined)}
          onClick={() => {
            setMapCenter(undefined)
          }}
          onTilesLoaded={handleTilesLoaded}>
          {nearestStores.slice(0, MAX_STORE_MARKER).map(store => (
            <MemoizedStoreMarker
              key={store.id}
              store={store}
              isSelected={selectedStoreId === store.id}
              onClick={handleMarkerClick}
              onDismiss={onDismiss}
              href={getStoreUrl(store)}
            />
          ))}
        </MapContainer>
      </APIProvider>
    </div>
  )
}

export const InteractiveMap = dynamic(() => Promise.resolve(InteractiveMapComponent), { ssr: false })
